import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Avatar, type AvatarUser } from "@/components/ui/Avatar";
import { Icon } from "@/components/ui/Icon";
import { CountPill, type CountPillTone } from "@/components/memo/ui/CountPill";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { formatMemoDate } from "@/lib/format";
import { STATUS_PALETTE } from "@/lib/tokens";
import { t } from "@/lib/strings";
import {
  filterSeriesTasks,
  partitionByAssignee,
  seriesTaskCounts,
  type TaskFilter,
} from "@/lib/seriesTasks";
import { useMe } from "@/hooks/useMe";
import { useSeriesTasks } from "@/hooks/useSeriesTasks";
import type { MemoUser, SeriesTask, UUID } from "@/lib/types";

// Mirror the person-picker mapping (UserMultiSelect) so a MemoUser renders in
// the shared Avatar, which expects the derived AvatarUser shape.
function toAvatarUser(u: MemoUser): AvatarUser {
  const name = u.display_name ?? u.username ?? "?";
  return {
    id: u.id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(u.id),
    username: u.username,
    department_workspace_id: u.department_workspace_id ?? null,
    department_color: u.department_color ?? null,
  };
}

/** Frist pill tone: overdue (past due, still open) → danger; a future/today due
 * on an open task → warn; done or no due → neutral. `today` is an ISO
 * "YYYY-MM-DD" string (lexicographic compare is valid for ISO dates). */
function fristTone(task: SeriesTask, today: string): CountPillTone {
  if (task.done || !task.due) return "neutral";
  return task.due < today ? "overdue" : "open";
}

const doneBoxBg = STATUS_PALETTE.green.dot;

function FilterPill({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        borderRadius: 999,
        padding: "5px 12px",
        fontSize: 12.5,
        fontWeight: 600,
        cursor: "pointer",
        background: active ? "var(--ink-1)" : "transparent",
        color: active ? "var(--surface)" : "var(--ink-2)",
        border: active ? "1px solid var(--ink-1)" : "1px solid var(--hairline-strong)",
      }}
    >
      {label}
      <span style={{ opacity: 0.7 }}>· {count}</span>
    </button>
  );
}

export function SeriesTasksList({ seriesId, boardId }: { seriesId: UUID; boardId: UUID | null }) {
  const { data: tasks = [] } = useSeriesTasks(seriesId);
  const { data: me } = useMe();
  const [filter, setFilter] = useState<TaskFilter>("all");
  const [onlyMine, setOnlyMine] = useState(false);

  const counts = useMemo(() => seriesTaskCounts(tasks), [tasks]);
  const visible = useMemo(() => filterSeriesTasks(tasks, filter), [tasks, filter]);
  const groups = useMemo(() => partitionByAssignee(visible, me?.id ?? null), [visible, me?.id]);
  const mineCount = useMemo(
    () => partitionByAssignee(tasks, me?.id ?? null).mine.length,
    [tasks, me?.id],
  );

  // 🔴 GROUPED, not filtered. The room sees every to-do — accountability in a Besprechungsreihe
  // is public, and hiding other people's work would quietly turn a shared roster into a private
  // one. Mine simply come FIRST, because that is what I open this tab to find. "Nur meine" is
  // the explicit opt-in for someone who wants only their own.
  //
  // Headers appear only when BOTH groups have something in them: a lone "MEINE AUFGABEN" over
  // the whole list, or over nothing, is noise dressed as structure.
  const sections = useMemo(() => {
    if (onlyMine) return [{ key: "mine", title: null, tasks: groups.mine }];
    if (groups.mine.length > 0 && groups.others.length > 0) {
      return [
        { key: "mine", title: t.seriesTasksMine, tasks: groups.mine },
        { key: "others", title: t.seriesTasksOthers, tasks: groups.others },
      ];
    }
    return [{ key: "all", title: null, tasks: visible }];
  }, [onlyMine, groups, visible]);
  // Local calendar day (not UTC) so an overdue task turns red at local midnight,
  // not hours late — `due` is a local date string.
  const today = useMemo(() => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }, []);

  if (tasks.length === 0) {
    return (
      <div style={{ color: "var(--ink-3)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>
        {t.tasksEmpty}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <FilterPill
          active={filter === "all"}
          label={t.filterAll}
          count={counts.all}
          onClick={() => setFilter("all")}
        />
        <FilterPill
          active={filter === "open"}
          label={t.filterUnchecked}
          count={counts.open}
          onClick={() => setFilter("open")}
        />
        <FilterPill
          active={filter === "done"}
          label={t.filterChecked}
          count={counts.done}
          onClick={() => setFilter("done")}
        />
        {/* Only offered when the user actually HAS a to-do here — a "Nur meine · 0" pill is a
            button whose only outcome is an empty list. */}
        {mineCount > 0 ? (
          <FilterPill
            active={onlyMine}
            label={t.seriesTasksOnlyMine}
            count={mineCount}
            onClick={() => setOnlyMine((v) => !v)}
          />
        ) : null}
      </div>

      {sections.map((section) => (
        <div key={section.key} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {section.title ? (
            <h3
              style={{
                margin: "4px 0 0",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--ink-3)",
              }}
            >
              {section.title}
              <span style={{ marginLeft: 6, fontWeight: 600, opacity: 0.7 }}>
                {section.tasks.length}
              </span>
            </h3>
          ) : null}
          {section.tasks.map((task) => {
            const metaLabel = task.origin_section ?? task.status_label ?? null;
            const originDate = task.origin_memo_date
              ? `${t.taskOriginPrefix} ${formatMemoDate(task.origin_memo_date)}`
              : null;
            const metaLine = [metaLabel, originDate].filter(Boolean).join(" · ");
            const tone = fristTone(task, today);
            const fristLabel = task.due ? formatMemoDate(task.due) : t.taskNoDue;

            const inner = (
              <>
                <span
                  aria-hidden
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: task.done ? doneBoxBg : "transparent",
                    border: task.done
                      ? `1px solid ${doneBoxBg}`
                      : "1px solid var(--hairline-strong)",
                  }}
                >
                  {task.done ? <Icon name="check" size={12} color="#fff" /> : null}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: task.done ? "var(--ink-4)" : "var(--ink-1)",
                      textDecoration: task.done ? "line-through" : "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {task.title}
                  </div>
                  {metaLine ? (
                    <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginTop: 2 }}>
                      {metaLine}
                    </div>
                  ) : null}
                  {task.done && task.solution ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12.5,
                        color: "var(--ink-3)",
                        marginTop: 3,
                      }}
                    >
                      <Icon name="check" size={12} color="var(--ink-4)" />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {task.solution}
                      </span>
                    </div>
                  ) : null}
                </div>
                <CountPill tone={tone}>{fristLabel}</CountPill>
                {task.assignee ? <Avatar user={toAvatarUser(task.assignee)} size={20} /> : null}
              </>
            );

            const rowStyle = {
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              border: "1px solid var(--hairline)",
              borderRadius: 10,
              textDecoration: "none",
              background: "var(--surface)",
            } as const;

            return boardId ? (
              <Link
                key={task.row_id}
                to="/b/$boardId"
                params={{ boardId }}
                search={{ row: task.row_id } as Record<string, string>}
                style={rowStyle}
              >
                {inner}
              </Link>
            ) : (
              <div key={task.row_id} style={rowStyle}>
                {inner}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
